import {
    getDropdownElements,
    toggleDropdown,
    adjustDropdownPosition,
    handleDropdownKeyboard,
    highlightOption,
    setupOptionsClick,
    setupDropdownTabulation,
    DropdownElements,
    DropdownPositionContext
} from '../shared/preparers/dropdown-core';

export class DropdownFieldPreparer {
    private readonly fieldElement: HTMLElement;
    private readonly rootNode: Document | ShadowRoot;
    private elements: DropdownElements | null = null;
    private selectedOptionIndex = -1;

    private rootClickHandler: ((event: MouseEvent) => void) | null = null;
    private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
    private focusHandler: (() => void) | null = null;
    private blurHandler: (() => void) | null = null;

    constructor(fieldElement: HTMLElement) {
        this.fieldElement = fieldElement;
        // Get the root node (Document or ShadowRoot) for proper event handling
        this.rootNode = fieldElement.getRootNode() as Document | ShadowRoot;
    }

    public prepare(): void {
        this.elements = getDropdownElements(this.fieldElement);
        if (!this.elements) {
            return;
        }

        setupDropdownTabulation(this.fieldElement);
        this.setupDropdownClick();
        setupOptionsClick(this.elements, {
            onChange: () => this.selectedOptionIndex = -1
        });
        this.setupKeyboardNavigation();
        this.setupFocusHandlers();
        this.setupRootClick();
        this.setupDropdownIconHover();
    }

    public destroy(): void {
        if (this.rootClickHandler) {
            (this.rootNode as EventTarget).removeEventListener('click', this.rootClickHandler as EventListener);
            this.rootClickHandler = null;
        }
        if (this.elements?.container) {
            if (this.keydownHandler) {
                this.elements.container.removeEventListener('keydown', this.keydownHandler);
            }
            if (this.focusHandler) {
                this.elements.container.removeEventListener('focus', this.focusHandler);
            }
            if (this.blurHandler) {
                this.elements.container.removeEventListener('blur', this.blurHandler);
            }
        }
    }

    private setupDropdownClick(): void {
        if (!this.elements) return;
        const { container } = this.elements;

        container.onclick = (event: MouseEvent) => {
            if ((event.target as HTMLElement).classList.contains('option')) {
                return;
            }
            const isActive = toggleDropdown(container);
            if (isActive) {
                this.selectedOptionIndex = -1;
                this.adjustPosition();
            }
        };
    }

    private adjustPosition(): void {
        if (!this.elements) return;

        const context: DropdownPositionContext = {
            viewportHeight: window.innerHeight,
            frameTop: 0
        };

        adjustDropdownPosition(this.elements, context);
    }

    private setupKeyboardNavigation(): void {
        if (!this.elements) return;
        const { container } = this.elements;

        this.keydownHandler = (event: KeyboardEvent) => {
            if (!this.elements) return;

            const newIndex = handleDropdownKeyboard(
                event,
                this.elements,
                this.selectedOptionIndex,
                { onChange: () => this.selectedOptionIndex = -1 }
            );

            if (newIndex !== this.selectedOptionIndex) {
                this.selectedOptionIndex = newIndex;
                if (newIndex >= 0) {
                    highlightOption(this.elements.optionItems, newIndex);
                }
            }
        };

        container.addEventListener('keydown', this.keydownHandler);
    }

    private setupFocusHandlers(): void {
        if (!this.elements) return;
        const { container, input } = this.elements;

        this.focusHandler = () => {
            input?.classList.add('dropdown-active-focus-effect');
        };

        this.blurHandler = () => {
            input?.classList.remove('dropdown-active-focus-effect');
        };

        container.addEventListener('focus', this.focusHandler);
        container.addEventListener('blur', this.blurHandler);

        // Focusout to close dropdown
        container.addEventListener('focusout', (event: FocusEvent) => {
            if (this.fieldElement.contains(event.relatedTarget as Node)) {
                return;
            }
            toggleDropdown(container, false);
        });
    }

    private setupRootClick(): void {
        if (!this.elements) return;
        const { container } = this.elements;

        this.rootClickHandler = (event: MouseEvent) => {
            // Use composedPath for Shadow DOM support
            const path = event.composedPath();
            const clickedInsideDropdown = path.includes(container);

            if (!clickedInsideDropdown) {
                toggleDropdown(container, false);
            }
        };

        // Listen on the root node (ShadowRoot or Document)
        (this.rootNode as EventTarget).addEventListener('click', this.rootClickHandler as EventListener);
    }

    private setupDropdownIconHover(): void {
        if (!this.elements) return;
        const { container, input } = this.elements;

        const dropdownIcon = this.fieldElement.querySelector<HTMLElement>('.dropdown-icon');
        if (!dropdownIcon) return;

        dropdownIcon.addEventListener('mouseover', () => {
            const isActive = container.classList.contains('active');
            // Check activeElement in the correct root
            const activeElement = this.rootNode instanceof ShadowRoot
                ? this.rootNode.activeElement
                : document.activeElement;
            const isInputFocused = input === activeElement;
            if (!isActive && !isInputFocused) {
                input?.classList.add('dropdown-border-hover-effect');
            }
            input?.classList.add('dropdown-hover-effect');
        });

        dropdownIcon.addEventListener('mouseout', () => {
            input?.classList.remove('dropdown-border-hover-effect');
            input?.classList.remove('dropdown-hover-effect');
        });
    }
}

export function initializeDropdownFields(container: HTMLElement): DropdownFieldPreparer[] {
    const dropdownElements = container.querySelectorAll<HTMLElement>('[data-field="dropdown"]');
    const preparers: DropdownFieldPreparer[] = [];

    dropdownElements.forEach(element => {
        const preparer = new DropdownFieldPreparer(element);
        preparer.prepare();
        preparers.push(preparer);
    });

    return preparers;
}
